import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { Select } from '../components/Select.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

type PeriodType = 'monthly' | 'quarterly';
type GoalKey =
  | 'response_sla'
  | 'billable_pct'
  | 'csat'
  | 'project_on_time'
  | 'team_retention';
type GoalStatus = 'hit' | 'miss' | 'ineligible' | 'not_applicable' | 'insufficient_data';

interface Bonus {
  id: string;
  goalKey: GoalKey;
  scope: 'individual' | 'team';
  bonusFtUsd: number;
  bonusPtUsd: number | null;
  updatedAt: string;
}

interface IndividualResult {
  goalKey: GoalKey;
  periodType: PeriodType;
  periodKey: string;
  status: GoalStatus;
  actualValue: number | null;
  detail: string;
  bonusUsd: number;
  bonusIfHitUsd: number;
}

interface PreviewRow {
  agentId: string;
  agentName: string;
  isFullTime: boolean;
  results: IndividualResult[];
  teamRetention?: {
    status: GoalStatus;
    retentionPct: number | null;
    detail: string;
    agentBonusUsd: number;
    bonusFtUsd: number;
    bonusPtUsd: number;
  };
}

interface PreviewResponse {
  periodType: PeriodType;
  periodKey: string;
  rows: PreviewRow[];
  teamRetention?: {
    status: GoalStatus;
    retentionPct: number | null;
    detail: string;
    activeAtStart: number;
    cancellations: number;
    bonusFtUsd: number;
    bonusPtUsd: number;
  };
}

const GOAL_LABEL: Record<GoalKey, string> = {
  response_sla: 'Response SLA',
  billable_pct: 'Billable %',
  csat: 'CSAT',
  project_on_time: 'Project on-time',
  team_retention: 'Team retention',
};

const MONTHLY_GOALS: GoalKey[] = ['response_sla', 'billable_pct'];
const QUARTERLY_GOALS: GoalKey[] = ['csat', 'project_on_time', 'team_retention'];

function monthOptions(count = 12): { key: string; label: string }[] {
  const now = new Date();
  const out: { key: string; label: string }[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    out.push({ key, label });
  }
  return out;
}

function quarterOptions(count = 8): { key: string; label: string }[] {
  const now = new Date();
  const out: { key: string; label: string }[] = [];
  let y = now.getFullYear();
  let q = Math.floor(now.getMonth() / 3) + 1;
  for (let i = 0; i < count; i++) {
    out.push({ key: `${y}-Q${q}`, label: `${y} Q${q}` });
    q -= 1;
    if (q < 1) {
      q = 4;
      y -= 1;
    }
  }
  return out;
}

function fmtUsd(n: number): string {
  return `$${n.toFixed(0)}`;
}

function statusCellClass(s: GoalStatus): string {
  switch (s) {
    case 'hit':
      return 'text-emerald-700 font-semibold';
    case 'miss':
      return 'text-red-700 font-semibold';
    case 'ineligible':
      return 'text-amber-700';
    default:
      return 'text-[var(--color-muted)]';
  }
}

export function AdminGoalsPage(): React.ReactElement {
  useDocumentTitle('Admin — Goals & Bonuses');
  const { agent } = useAuth();
  const monthList = useMemo(monthOptions, []);
  const quarterList = useMemo(quarterOptions, []);
  const [periodType, setPeriodType] = useState<PeriodType>('monthly');
  const [periodKey, setPeriodKey] = useState(monthList[0]?.key ?? '');
  const [bonuses, setBonuses] = useState<Bonus[]>([]);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function onChangePeriodType(t: PeriodType) {
    setPeriodType(t);
    setPeriodKey(t === 'monthly' ? monthList[0]?.key ?? '' : quarterList[0]?.key ?? '');
  }

  async function load() {
    setError(null);
    if (!periodKey) return;
    try {
      const [b, p] = await Promise.all([
        apiFetch<{ bonuses: Bonus[] }>(
          `/api/admin/goals/bonuses?periodType=${periodType}&periodKey=${encodeURIComponent(periodKey)}`,
        ),
        apiFetch<PreviewResponse>(
          `/api/admin/goals/preview?periodType=${periodType}&periodKey=${encodeURIComponent(periodKey)}`,
        ),
      ]);
      setBonuses(b.bonuses);
      setPreview(p);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load.');
    }
  }

  useEffect(() => {
    void load();
  }, [periodType, periodKey]);

  if (!agent) return <p>Loading…</p>;
  if (agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  async function saveBonus(b: Bonus, patch: { bonusFtUsd?: number; bonusPtUsd?: number | null }) {
    setBusy(true);
    setError(null);
    try {
      await apiFetch('/api/admin/goals/bonuses', {
        method: 'PUT',
        json: {
          periodType,
          periodKey,
          goalKey: b.goalKey,
          scope: b.scope,
          bonusFtUsd: patch.bonusFtUsd ?? b.bonusFtUsd,
          bonusPtUsd: patch.bonusPtUsd !== undefined ? patch.bonusPtUsd : b.bonusPtUsd,
        },
      });
      await load();
      setInfo('Bonus updated.');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function closePeriod() {
    if (!confirm(`Close ${periodKey}? This snapshots results for all agents. You can reopen it if needed.`)) return;
    setBusy(true);
    setError(null);
    try {
      const r = await apiFetch<{ snapshotsWritten: number }>('/api/admin/goals/close', {
        method: 'POST',
        json: { periodType, periodKey },
      });
      await load();
      setInfo(`Closed. ${r.snapshotsWritten} snapshot rows written.`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Close failed.');
    } finally {
      setBusy(false);
    }
  }

  async function reopenPeriod() {
    if (
      !confirm(
        `Reopen ${periodKey}? This deletes all snapshot rows for the period and lets live numbers move again until you close it.`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      const r = await apiFetch<{ snapshotsDeleted: number }>('/api/admin/goals/reopen', {
        method: 'POST',
        json: { periodType, periodKey },
      });
      await load();
      setInfo(`Reopened. ${r.snapshotsDeleted} snapshot rows removed.`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Reopen failed.');
    } finally {
      setBusy(false);
    }
  }

  function downloadCsv() {
    const url = `/api/admin/goals/export.csv?periodType=${periodType}&periodKey=${encodeURIComponent(periodKey)}`;
    window.location.href = url;
  }

  const isClosed = Boolean(
    preview?.rows.some((row) => row.results.some((r) => r.detail === '(closed period)')),
  );

  const periodGoals = periodType === 'monthly' ? MONTHLY_GOALS : QUARTERLY_GOALS;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-3xl font-bold text-[var(--color-header)]">Goals & Bonuses</h1>
        <p className="mt-1 text-[var(--color-muted)]">
          Set bonus amounts per goal per period, preview where every tech stands today, and close the
          period when you're ready to pay. Closed periods snapshot the math — later edits to the
          dollar amounts don't change history. Thresholds (95% CSAT, 2h SLA, 50% billable, 100%
          retention) are fixed in code.
        </p>
      </div>

      {info && (
        <Card className="border-green-300 bg-green-50/40">
          <p className="text-sm text-green-900">{info}</p>
        </Card>
      )}
      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      <Card className="!p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select
            label="Period type"
            value={periodType}
            onChange={(e) => onChangePeriodType(e.target.value as PeriodType)}
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
          </Select>
          <Select
            label={periodType === 'monthly' ? 'Month' : 'Quarter'}
            value={periodKey}
            onChange={(e) => setPeriodKey(e.target.value)}
          >
            {(periodType === 'monthly' ? monthList : quarterList).map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </Select>
          <div className="ml-auto flex items-end gap-2">
            <Button variant="outline" onClick={downloadCsv} disabled={busy}>
              Export CSV
            </Button>
            {isClosed ? (
              <Button variant="danger" onClick={() => void reopenPeriod()} disabled={busy}>
                Reopen period
              </Button>
            ) : (
              <Button onClick={() => void closePeriod()} disabled={busy}>
                Close period
              </Button>
            )}
          </div>
        </div>
        {isClosed && (
          <p className="mt-3 text-xs text-amber-700">
            This period is CLOSED. Bonus edits are blocked until reopened.
          </p>
        )}
      </Card>

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Bonus amounts</h2>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Dollar value paid per agent when each goal is hit. Part-time tier is blank when the
          goal doesn't apply to part-timers (only Billable %).
        </p>
        <table className="mt-4 w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
            <tr>
              <th className="pb-2">Goal</th>
              <th className="pb-2 w-32">Full-time $</th>
              <th className="pb-2 w-32">Part-time $</th>
              <th className="pb-2 text-xs">Updated</th>
            </tr>
          </thead>
          <tbody>
            {periodGoals.map((gk) => {
              const b = bonuses.find((x) => x.goalKey === gk);
              if (!b) {
                return (
                  <tr key={gk} className="border-t border-[var(--color-border)]">
                    <td className="py-2">{GOAL_LABEL[gk]}</td>
                    <td colSpan={3} className="py-2 text-sm text-[var(--color-muted)]">
                      No bonus row seeded for this period — re-run migrations or insert manually.
                    </td>
                  </tr>
                );
              }
              return <BonusRow key={b.id} bonus={b} onSave={saveBonus} disabled={busy || isClosed} />;
            })}
          </tbody>
        </table>
      </Card>

      {preview && (
        <Card className="!p-0 overflow-hidden">
          <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3">
            <h2 className="text-base font-semibold text-[var(--color-header)]">
              {isClosed ? 'Snapshot' : 'Live preview'} — {periodType === 'monthly' ? 'monthly' : 'quarterly'} {periodKey}
            </h2>
            {preview.teamRetention && (
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Team retention: {preview.teamRetention.detail}
              </p>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <tr>
                  <th className="px-3 py-2">Agent</th>
                  <th className="px-3 py-2">FT/PT</th>
                  {periodGoals.map((gk) => (
                    <th key={gk} className="px-3 py-2">{GOAL_LABEL[gk]}</th>
                  ))}
                  <th className="px-3 py-2 text-right">Total $</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => {
                  let total = row.results.reduce((s, r) => s + r.bonusUsd, 0);
                  if (row.teamRetention) total += row.teamRetention.agentBonusUsd;
                  return (
                    <tr key={row.agentId} className="border-t border-[var(--color-border)]">
                      <td className="px-3 py-2 font-medium">{row.agentName}</td>
                      <td className="px-3 py-2 text-xs text-[var(--color-muted)]">
                        {row.isFullTime ? 'FT' : 'PT'}
                      </td>
                      {periodGoals.map((gk) => {
                        if (gk === 'team_retention') {
                          if (!row.teamRetention) {
                            return (
                              <td key={gk} className="px-3 py-2 text-[var(--color-muted)]">—</td>
                            );
                          }
                          return (
                            <td key={gk} className="px-3 py-2">
                              <div className={statusCellClass(row.teamRetention.status)}>
                                {row.teamRetention.status.toUpperCase()}
                              </div>
                              <div className="text-xs text-[var(--color-muted)]">
                                {fmtUsd(row.teamRetention.agentBonusUsd)}
                              </div>
                            </td>
                          );
                        }
                        const r = row.results.find((rr) => rr.goalKey === gk);
                        if (!r) return <td key={gk} className="px-3 py-2 text-[var(--color-muted)]">—</td>;
                        return (
                          <td key={gk} className="px-3 py-2">
                            <div className={statusCellClass(r.status)}>{r.status.toUpperCase()}</div>
                            <div className="text-xs text-[var(--color-muted)]">
                              {r.actualValue === null ? '—' : `${(r.actualValue * 100).toFixed(1)}%`} · {fmtUsd(r.bonusUsd)}
                            </div>
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-right font-mono font-semibold">{fmtUsd(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="border-t border-[var(--color-border)] bg-[var(--color-surface)]">
                <tr>
                  <td className="px-3 py-2 text-xs uppercase tracking-wide text-[var(--color-muted)]" colSpan={periodGoals.length + 2}>
                    Period total
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-bold">
                    {fmtUsd(
                      preview.rows.reduce(
                        (s, row) =>
                          s +
                          row.results.reduce((a, r) => a + r.bonusUsd, 0) +
                          (row.teamRetention?.agentBonusUsd ?? 0),
                        0,
                      ),
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function BonusRow({
  bonus,
  onSave,
  disabled,
}: {
  bonus: Bonus;
  onSave: (b: Bonus, patch: { bonusFtUsd?: number; bonusPtUsd?: number | null }) => void | Promise<void>;
  disabled: boolean;
}): React.ReactElement {
  const [ft, setFt] = useState(String(bonus.bonusFtUsd));
  const [pt, setPt] = useState(bonus.bonusPtUsd === null ? '' : String(bonus.bonusPtUsd));
  const dirty = ft !== String(bonus.bonusFtUsd) || pt !== (bonus.bonusPtUsd === null ? '' : String(bonus.bonusPtUsd));

  useEffect(() => {
    setFt(String(bonus.bonusFtUsd));
    setPt(bonus.bonusPtUsd === null ? '' : String(bonus.bonusPtUsd));
  }, [bonus]);

  function save() {
    const ftNum = Number(ft);
    const ptNum = pt.trim() === '' ? null : Number(pt);
    if (!Number.isFinite(ftNum) || ftNum < 0) return;
    if (ptNum !== null && (!Number.isFinite(ptNum) || ptNum < 0)) return;
    void onSave(bonus, { bonusFtUsd: ftNum, bonusPtUsd: ptNum });
  }

  return (
    <tr className="border-t border-[var(--color-border)]">
      <td className="py-2 pr-2">{GOAL_LABEL[bonus.goalKey]}</td>
      <td className="py-2 pr-2">
        <input
          type="number"
          step="0.01"
          min="0"
          value={ft}
          onChange={(e) => setFt(e.target.value)}
          onBlur={() => dirty && save()}
          disabled={disabled}
          className="block w-full rounded border border-[var(--color-border)] bg-white px-2 py-1.5 text-sm outline-none focus:border-[var(--color-t3-blue)] disabled:bg-[var(--color-surface)]"
        />
      </td>
      <td className="py-2 pr-2">
        <input
          type="number"
          step="0.01"
          min="0"
          value={pt}
          onChange={(e) => setPt(e.target.value)}
          onBlur={() => dirty && save()}
          placeholder={bonus.goalKey === 'billable_pct' ? 'N/A' : ''}
          disabled={disabled || bonus.goalKey === 'billable_pct'}
          className="block w-full rounded border border-[var(--color-border)] bg-white px-2 py-1.5 text-sm outline-none focus:border-[var(--color-t3-blue)] disabled:bg-[var(--color-surface)]"
        />
      </td>
      <td className="py-2 text-xs text-[var(--color-muted)]">
        {new Date(bonus.updatedAt).toLocaleString()}
      </td>
    </tr>
  );
}
